/** Reverse geocode via shipping MasterLocation (same as main portal GoogleAddressMap). */

/**
 * Unwrap common API envelopes and stringified JSON; normalize countryCode shape.
 * @param {unknown} parsed
 * @returns {object|null}
 */
export function normalizeReverseGeocodeBody(parsed) {
  let o = parsed;
  if (o == null) return null;
  if (typeof o === "string") {
    try {
      o = JSON.parse(o);
    } catch {
      return null;
    }
  }
  if (!o || typeof o !== "object") return null;
  if (o.data != null && typeof o.data === "object" && !Array.isArray(o.data)) {
    o = o.data;
  }
  if (o.data != null && typeof o.data === "object" && !Array.isArray(o.data)) {
    o = o.data;
  }
  const countryCode =
    o.countryCode ?? o.country_code ?? o.CountryCode ?? o.countryCodeIso ?? null;
  const country = o.country ?? o.countryName ?? null;
  return {
    ...o,
    ...(countryCode != null && String(countryCode).trim() !== ""
      ? { countryCode: String(countryCode).trim() }
      : {}),
    ...(country != null && String(country).trim() !== "" ? { country: String(country).trim() } : {}),
  };
}

function normalizeEmxServicesBase() {
  const raw = process.env.REACT_APP_EMX_SERVICES;
  if (!raw || typeof raw !== "string") return "";
  return raw.replace(/\/?$/, "/");
}

/**
 * @param {number} lat
 * @param {number} lng
 * @param {{ accessToken?: string, locale?: string, provider?: number }} [options]
 * @returns {Promise<object|null>} Parsed JSON body, or null if unavailable
 */
export async function fetchReverseByProvider(lat, lng, options = {}) {
  const { accessToken, locale, provider = 0 } = options;
  const base = normalizeEmxServicesBase();
  if (!base || typeof lat !== "number" || typeof lng !== "number" || Number.isNaN(lat) || Number.isNaN(lng)) {
    return null;
  }

  const params = new URLSearchParams({
    Longitude: String(lng),
    Latitude: String(lat),
    Provider: String(provider),
  });
  const url = `${base}shipping/api/MasterLocation/ReverseByProvider?${params.toString()}`;

  const headers = {
    accept: "application/json",
  };
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }
  if (locale) {
    headers["Accept-Language"] = locale;
  }

  try {
    const response = await fetch(url, { headers });
    if (!response.ok) return null;
    const raw = await response.json();
    return normalizeReverseGeocodeBody(raw);
  } catch {
    return null;
  }
}

/** In-flight requests keyed by rounded lat/lng so duplicate callbacks share one network call. */
const pendingReverseByCoordKey = new Map();

/**
 * Same as {@link fetchReverseByProvider} but coalesces concurrent calls for the same coordinates
 * (Google Geocoder / Places can invoke handlers twice for one user action).
 */
export function fetchReverseByProviderDeduped(lat, lng, options = {}) {
  const key = `${Number(lat).toFixed(6)},${Number(lng).toFixed(6)}`;
  const existing = pendingReverseByCoordKey.get(key);
  if (existing) return existing;

  const promise = fetchReverseByProvider(lat, lng, options).finally(() => {
    pendingReverseByCoordKey.delete(key);
  });
  pendingReverseByCoordKey.set(key, promise);
  return promise;
}

/**
 * Ensures countryCode is set from ReverseByProvider when coords exist but code is missing
 * (handles Proceed before the async map callback finishes).
 */
export async function ensureCountryCodeFromReverseIfNeeded(addr, { accessToken, locale, provider = 0 } = {}) {
  if (!addr || typeof addr !== "object" || !addr.coords) return addr;
  const existing = addr.countryCode ?? addr.country_code;
  if (existing !== undefined && existing !== null && String(existing).trim() !== "") {
    return addr;
  }
  const lat = Number(addr.coords.lat);
  const lng = Number(addr.coords.lng);
  if (Number.isNaN(lat) || Number.isNaN(lng)) return addr;
  const reverse = await fetchReverseByProviderDeduped(lat, lng, {
    accessToken,
    locale,
    provider,
  });
  if (!reverse) return addr;
  const code = reverse.countryCode ?? reverse.country_code;
  if (code === undefined || code === null || String(code).trim() === "") return addr;
  return {
    ...addr,
    countryCode: String(code).trim(),
    ...(reverse.country ? { country: reverse.country } : {}),
  };
}
